import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { BillTemplateGet } from "../../../data/Types"
import { P } from "../../../auth/permissions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { ReactComponent as PLUSINCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { ReactComponent as DOWNLOAD } from "../../../assets/DownloadICON.svg"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import ExportBillsModal from "../components/ExportBillsModal"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import AddBillTemplateModal from "../components/addBillModal/AddBillTemplateModal"
import BillTemplateTableRow from "../components/billTemplate/BillTemplateTableRow"
import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import FloatingActionButton from "../components/FloatingActionButton"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { Authorized } from "../../../components/authentication/Authorized"
export default function BillTemplatePage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.BILL_TEMPLATE_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const [filterCategorie, setfilterCategorie] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "categorie"))
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "dateSince"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "dateTo"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "invertSort"))
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "archived"))
    const [filterCanceled, setfilterCanceled] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_TEMPLATE_FILTER + "canceled"))

    useResetPageOnFilterChange([searchParams, filterCategorie, filterDateSince, filterDateTo, invertSort, filterArchived, filterCanceled], () =>
        handlePageChange(1)
    )
    
    // Get Data Query
    const queryKey =
        GeneralQueryKeys.BILL_TEMPLATE_ALL + page + searchParams + filterDateSince + filterDateTo + filterArchived + filterCategorie + invertSort + filterCanceled
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "bill-template",
                page: page,
                limit: 15,
                search: searchParams,
                startDate: filterDateSince,
                endDate: filterDateTo,
                archived: filterArchived,
                billTemplateCategorie: filterCategorie,
                invertSort: invertSort,
                canceled: filterCanceled,
            }),
    })

    const modalCon = useContext(ModalContext)

    const [showFilter, setshowFilter] = useState(false)

    const handleRemoveFilter = () => {
        setfilterCategorie("")
        setfilterDateTo("")
        setfilterDateSince("")
        setfilterArchived(false)
        setfilterCanceled(undefined)
    }

    // FRONTEND LOGIC
    const displayedBillTemplates = useMemo(() => data?.docs, [data?.docs])
    const billTemplateTableColumns: ColumnType[] = [
        {
            label: "Erstellungsdatum",
            responsive: "hidden md:table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Kategorie",
            responsive: "hidden lg:table-cell",
            width: " min-w-[9rem] w-fit",
        },
        {
            label: "Intervall",
            responsive: "hidden sm:table-cell",
            width: " min-w-[9rem] w-fit",
        },
        {
            label: "Netto",
            width: "sm:min-w-[8rem] w-fit",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Brutto",
            width: "sm:min-w-[8rem] w-fit",
            responsive: "hidden 2xl:table-cell",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Kunde",
            responsive: "table-cell ",
        },
        {
            label: "Objekt",
            responsive: "hidden xl:table-cell ",
        },
        {
            label: "Status",
            responsive: "table-cell ",
        },
    ]

    return (
        <BasePage>
            <HeadBanner
                title={"Rechnungsvorlagen"}
                button={
                    <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
                        <div className="flex gap-4">
                            <DOWNLOAD className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(3)} />
                            <PLUSINCIRCLE className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(1)} />
                        </div>
                    </Authorized>
                }
            />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Kunde / Objekt suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps=" overflow-x-hidden my-4">
                        <BaseTable
                            columns={billTemplateTableColumns}
                            tbody={displayedBillTemplates?.map((item: BillTemplateGet) => (
                                <BillTemplateTableRow
                                    key={item._id}
                                    id={item._id}
                                    customer={item?.customer_id?.customer}
                                    date={item.date}
                                    object={item?.object_id ?? "Keine Angabe"}
                                    netto={item.totalNetto}
                                    brutto={item.totalBrutto}
                                    categorie={item.billTemplateCategorie}
                                    intervalMonths={item.intervalMonths}
                                    archived={item.archived}
                                    canceled={item.canceled}
                                    state={item.canceled ? "Storniert" : item.archived ? "Archiviert" : "Aktiv"}
                                />
                            ))}
                        />
                        {(!displayedBillTemplates || displayedBillTemplates.length === 0) && (
                            <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erstellten Rechnungsvorlagen</p>
                        )}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        searchdateSince={filterDateSince}
                        searchdateTo={filterDateTo}
                        changeDateTo={setfilterDateTo}
                        changeDateSince={setfilterDateSince}
                        removeFilter={handleRemoveFilter}
                        searchbillCategorie={filterCategorie}
                        changebillCategorie={setfilterCategorie}
                        setVisible={() => setshowFilter(!showFilter)}
                        searchArchived={filterArchived}
                        changeArchived={setfilterArchived}
                        searchCanceled={filterCanceled}
                        changeCanceled={setfilterCanceled}
                        statusType={"bill"}
                        sessionStorageKey={LocalStorageKeys.BILL_TEMPLATE_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            pageSize={15}
                            defaultCurrent={1}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}
            <FloatingActionButton currentPage="bill-template" />
            <AddBillTemplateModal queryKey={queryKey} modalId={1} />
            <ExportBillsModal modalId={3} close={() => modalCon?.trigger(3)} />
        </BasePage>
    )
}
